// Handle navigation for smaller screen sizes
(function () {
    "use strict";

    // Utility: Quick element builder
    function el(tag, props = {}, children = []) {
        const element = document.createElement(tag);
        Object.entries(props).forEach(([key, val]) => {
            if (key === "className") element.className = val;
            else if (key === "dataset") Object.assign(element.dataset, val);
            else if (key.startsWith("on") && typeof val === "function") {
                element.addEventListener(key.slice(2).toLowerCase(), val);
            } else if (val !== null && val !== undefined) {
                element.setAttribute(key, val);
            }
        });
        children.forEach(child => {
            if (child) {
                element.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
            }
        });
        return element;
    }

    // Helper: Create standardized chevron images
    const createChevron = (src, className = "") =>
        el("img", { src, width: "24", height: "24", alt: "Show sub menu", className });

    /*
      Constructs a navigation <li> item
    */
    function constructElement(navElement, id, expanded, directionClass, icon, name, url, includeIconLink, isTop, hasChildren, leftIcon, maori, useIconForName) {
        const topValue = navElement.dataset.top;
        const selectedValue = navElement.dataset.selected;
        const href = url || "#";

        // 1. Build Class List
        const classNames = [];
        if (directionClass) classNames.push(directionClass);
        if (isTop) classNames.push("istop");
        if (hasChildren) classNames.push("dropdown");
        if (expanded) classNames.push("expanded");
        if (topValue === String(id)) {
            classNames.push("top");
            if (selectedValue === String(id)) classNames.push("expanded");
        }

        const li = el("li", { className: classNames.join(" "), dataset: { contentid: id } });

        // 2. Build Chevron / Toggle Icon Link
        const isBack = directionClass === "back";
        const iconlink = el("a", {
            href,
            className: directionClass || "",
            title: directionClass || "",
            onClick: (e) => {
                e.preventDefault();
                const handler = isBack ? backClick : submenuClick;
                if (typeof handler === "function") handler(e.currentTarget, navElement);
            }
        });

        if (isTop || isBack) {
            iconlink.appendChild(createChevron("/img/chevron-right.svg"));
            iconlink.appendChild(createChevron("/img/chevron-right-active.svg", "active"));
        } else {
            iconlink.appendChild(createChevron("/img/chevron-right-black.svg"));
        }

        // 3. Build Page Link Anchor Node
        const link = el("a", {
            href,
            onClick: (e) => {
                if (isBack && !isTop) {
                    e.preventDefault();
                    if (typeof backClick === "function") backClick(e.currentTarget, navElement);
                } else if (hasChildren) {
                    e.preventDefault();
                    if (typeof submenuClick === "function") submenuClick(iconlink, navElement);
                }
            },
            onMouseenter: () => {
                const closestNav = link.closest("nav");
                const closestLi = link.closest("li");
                if (closestNav?.classList.contains("sublinks") && closestLi?.classList.contains("dropdown") && !link.nextElementSibling) {
                    if (typeof subnavFlyout === "function") subnavFlyout(link);
                }
            }
        });

        if (isBack && !isTop) {
            link.classList.add("backlinker");
            iconlink.classList.add("backlink");
        }

        // Build Text Span
        const span = el("span", { className: "text" });
        if (isBack) {
            span.textContent = "Back";
        } else {
            if (leftIcon) {
                span.appendChild(el("i", { className: `lefticon ${leftIcon}` }));
            } else {
                span.classList.add("leftspace");
            }

            if (useIconForName && icon instanceof HTMLElement) {
                span.appendChild(icon);
            } else {
                span.appendChild(document.createTextNode(useIconForName ? icon : name));
            }
        }

        // Append Māori Text Translation
        if (maori) {
            link.appendChild(el("span", { className: "text leftspace", lang: "mi" }, [maori]));
            link.appendChild(el("br"));
        }
        link.appendChild(span);

        // 4. Spacer Node Configuration
        const spacer = el("a", { href, className: "submenu", title: "spacer" }, [
            el("span", { className: "text" })
        ]);

        // 5. Structure Layout Order
        if (isBack) {
            li.appendChild(includeIconLink ? iconlink : spacer);
            li.appendChild(link);
        } else {
            li.appendChild(link);
            if (includeIconLink) {
                iconlink.classList.add("has-submenu");
                iconlink.setAttribute("role", "button");
                iconlink.setAttribute("aria-expanded", "false");
                li.appendChild(iconlink);
            } else {
                li.appendChild(spacer);
            }
        }

        return li;
    }

    // Handles flyout hover subnav generation
    function subnavFlyout(link) {
        const closestLi = link?.closest("li");
        if (!closestLi) return;

        const contentid = closestLi.dataset.contentid;
        const url = `/api/mainnav/menuitems/?id=${contentid}&currentPageId=${contentid}`;

        fetch(url, { method: "POST", cache: "no-cache" })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(jsonData => {
                const data = jsonData.data ?? jsonData.Data;
                const items = data?.items ?? data?.Items;
                if (!Array.isArray(items)) return;

                const linksUl = el("ul", {}, items.map(item =>
                    el("li", {}, [
                        el("a", { href: item.Url || item.url }, [
                            el("span", { className: "text" }, [item.Name || item.name])
                        ])
                    ])
                ));

                const parentLi = link.parentElement;
                if (!parentLi) return;

                parentLi.appendChild(linksUl);

                let flyoutTimeout = null;
                parentLi.addEventListener("mouseenter", () => clearTimeout(flyoutTimeout));
                parentLi.addEventListener("mouseleave", () => {
                    clearTimeout(flyoutTimeout);
                    flyoutTimeout = setTimeout(() => {
                        parentLi.querySelectorAll("ul").forEach(ul => ul.remove());
                    }, 500);
                });

                // Clear sibling open dropdown lists
                parentLi.parentElement?.querySelectorAll(":scope > li").forEach(siblingLi => {
                    if (siblingLi.dataset.contentid !== String(contentid)) {
                        siblingLi.querySelectorAll("ul").forEach(ul => ul.remove());
                    }
                });
            })
            .catch(() => { /* Silent catch */ });
    }

    // Handle back click navigation step
    function backClick(submenu, navElement) {
        const id = submenu?.closest("li")?.dataset.contentid;
        if (!id) return;

        const currentPageId = navElement.dataset.selected;
        const url = `/api/mainnav/parentitems/?id=${id}&currentPageId=${currentPageId}`;

        if (typeof populateData === "function") {
            populateData(url, navElement);
        }
    }

    // Handle submenu step-down click navigation
    function submenuClick(submenu, navElement) {
        const id = submenu?.closest("li")?.dataset.contentid;
        if (!id) return;

        const currentPageId = navElement.dataset.selected;
        const url = `/api/mainnav/menuitems/?id=${id}&currentPageId=${currentPageId}`;

        if (typeof populateData === "function") {
            populateData(url, navElement);
        }

        // Smooth scroll to top of navigation container
        const mainNav = document.querySelector(".main-nav");
        if (mainNav) {
            const navTopPosition = mainNav.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: navTopPosition, behavior: "smooth" });
        }
    }

    /* Populates navigation container with requested level */
    function populateData(url, navElement, hideSubNavigation, includeHome) {
        fetch(url, { method: "POST", cache: "no-cache" })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(jsonData => {
                const data = jsonData;
                if (!data?.parentItem) return;

                const content = el("ul");
                let showTopLink = true;

                if (navElement.dataset.top === String(data.parentItem.id)) {
                    showTopLink = false;
                    content.classList.add("toplevel");
                }
                let includeDirectionLink = !data.parentItem.isTop;

                // 1. Home Icon Item
                if (includeHome) {
                    const homeIcon = el("i", { className: "fas fa-home" });
                    content.appendChild(constructElement(navElement, data.parentItem.id, data.parentItem.expanded, "mainmenu", homeIcon, data.parentItem.name, data.parentItem.url, false, data.parentItem.isTop, false, null, data.parentItem.maori, true));
                }

                // 2. Back / Header Parent Link
                if (showTopLink) {
                    if (!hideSubNavigation) {
                        content.appendChild(constructElement(navElement, data.parentItem.id, data.parentItem.expanded, "back", "left", data.parentItem.name, data.parentItem.url, includeDirectionLink, data.parentItem.isTop, false));
                        content.appendChild(constructElement(navElement, data.parentItem.id, data.parentItem.expanded, "mainmenu", "", data.parentItem.name, data.parentItem.url, false, data.parentItem.isTop, false, null, data.parentItem.maori));
                    } else {
                        content.appendChild(el("li", {}, [
                            el("a", { className: "submenu" }),
                            el("a", { className: "plain" }, [data.parentItem.name])
                        ]));
                    }
                }

                // 3. Main Child Links
                if (Array.isArray(data.items)) {
                    data.items.forEach(item => {
                        includeDirectionLink = item.hasChildren && !hideSubNavigation;
                        content.appendChild(constructElement(navElement, item.id, item.expanded, "submenu", "right", item.name, item.url, includeDirectionLink, !showTopLink, item.hasChildren, item.icon, item.maori));
                    });
                }

                // Append generated items to root container
                navElement.replaceChildren(content);

                // Position sublinks flyout panel on desktop hover
                if (navElement.classList.contains("sublinks") && hideSubNavigation) {
                    const activeParent = navElement.parentElement?.querySelector(".hover");
                    if (activeParent) {
                        navElement.style.left = `${activeParent.offsetLeft}px`;
                        navElement.style.right = "";

                        const rightPos = navElement.offsetWidth + navElement.offsetLeft;
                        const maxRight = activeParent.parentElement?.offsetWidth || 0;

                        if (rightPos > maxRight) {
                            navElement.style.left = "";
                            navElement.style.right = "0px";
                        }
                    }
                }

                // Setup top level navigation hover states
                if (data.parentItem.isTop && !navElement.classList.contains("leftnav")) {
                    if (navElement.classList.contains("allowhover")) {
                        navElement.querySelectorAll("ul > li").forEach(li => {
                            li.addEventListener("mouseenter", () => {
                                if (!li.classList.contains("dropdown")) {
                                    navElement.querySelectorAll(".sublinks").forEach(e => e.remove());
                                    navElement.querySelectorAll(".hover").forEach(e => e.classList.remove("hover"));
                                }
                            });
                        });
                    }

                    let subNavTimeout = null;
                    navElement.querySelectorAll(".dropdown").forEach(dropdown => {
                        if (navElement.classList.contains("allowhover")) {
                            dropdown.addEventListener("mouseenter", (e) => {
                                if (!dropdown.classList.contains("hover")) {
                                    clickAction(e, dropdown, navElement);
                                }
                            });

                            navElement.addEventListener("mouseenter", () => clearTimeout(subNavTimeout));
                            navElement.addEventListener("mouseleave", () => {
                                clearTimeout(subNavTimeout);
                                subNavTimeout = setTimeout(() => {
                                    navElement.querySelectorAll(".sublinks").forEach(e => e.remove());
                                    navElement.querySelectorAll(".hover").forEach(e => e.classList.remove("hover"));
                                }, 500);
                            });
                        } else {
                            dropdown.addEventListener("click", (e) => clickAction(e, dropdown, navElement));
                        }
                    });
                }

                // Process complementary footer/top sub-links
                if (Array.isArray(jsonData.topLinks)) {
                    const linksWrapper = el("div", { className: "toplinks-links" });
                    jsonData.topLinks.forEach(link => {
                        linksWrapper.appendChild(el("a", { href: link.url }, [link.name]));
                        linksWrapper.appendChild(document.createTextNode(" "));
                    });
                    navElement.appendChild(el("div", { className: "toplinks" }, [linksWrapper]));
                }
            })
            .catch(() => { /* Silent catch */ });
    }

    // Handles large screen dropdown trigger actions
    function clickAction(e, element, navElement) {
        if (window.innerWidth >= 1200 && element.classList.contains("dropdown") && !element.classList.contains("top")) {
            e.preventDefault();

            if (element.classList.contains("hover")) {
                navElement.querySelectorAll(".sublinks").forEach(el => el.remove());
                element.classList.remove("hover");
            } else {
                navElement.querySelectorAll(".hover").forEach(el => el.classList.remove("hover"));
                element.classList.add("hover");
                navElement.querySelectorAll(".sublinks").forEach(el => el.remove());

                const sublinks = el("nav", {
                    className: "sublinks",
                    dataset: {
                        selected: navElement.dataset.selected,
                        top: element.dataset.contentid
                    }
                });

                navElement.appendChild(sublinks);

                const id = element.dataset.contentid;
                const currentPageId = navElement.dataset.selected;
                const url = `/api/mainnav/menuitems/?id=${id}&currentPageId=${currentPageId}`;

                populateData(url, sublinks, navElement.classList.contains("allowhover"));
            }
        }
    }

    // Reset layout UI to default baseline
    function resetMainMenu() {
        if (window.innerWidth < 1200) {
            const mainEl = document.querySelector("main");
            if (mainEl) mainEl.style.marginTop = "0";

            const headerEl = document.querySelector("header");
            if (headerEl) {
                headerEl.querySelector(".nav-container")?.classList.remove("fixed");
                headerEl.querySelectorAll(".mlogo").forEach(el => el.style.display = "");
                headerEl.querySelectorAll(".hlogo").forEach(el => el.style.display = "none");
            }
        }

        document.querySelectorAll(".navigation").forEach(navElement => {
            const id = navElement.dataset.top;
            const currentPageId = navElement.dataset.selected;
            const url = `/api/mainnav/menuitems/?id=${id}&currentPageId=${currentPageId}`;

            const navButtonsParent = navElement.closest(".nav-buttons");
            const navHome = navButtonsParent?.dataset.navhome;

            populateData(url, navElement, null, navHome);
        });
    }

    // Main entry point - page setup
    document.addEventListener("DOMContentLoaded", () => {
        let eventListenerAdded = false;

        function closeMenu() {
            document.querySelectorAll(".navbar-toggle .navbar-toggler-icon").forEach(icon => {
                icon.closest(".navbar-toggle")?.setAttribute("aria-expanded", "false");
            });

            document.querySelectorAll(".navbar-toggler-icon").forEach(el => el.style.display = "");
            document.querySelectorAll(".navbar-close").forEach(el => el.style.display = "none");
            document.querySelector("body.homepage header .navbar")?.classList.remove("mobile");
        }

        // Toggle mobile navigation UI controls
        document.querySelectorAll(".btn-toggle .navbar-toggle").forEach(toggleBtn => {
            toggleBtn.addEventListener("click", function (e) {
                e.preventDefault();

                const targetSelector = this.getAttribute("data-bs-target");
                const targets = targetSelector ? document.querySelectorAll(targetSelector) : [];

                targets.forEach(target => {
                    if (!target.classList.contains("open")) resetMainMenu();
                    target.classList.toggle("open");
                });

                if (!eventListenerAdded) {
                    document.body.addEventListener("keyup", (event) => {
                        if (event.key === "Escape") {
                            let anyOpen = false;
                            targets.forEach(target => {
                                if (target.classList.contains("open")) {
                                    target.classList.remove("open");
                                    anyOpen = true;
                                }
                            });
                            if (anyOpen) closeMenu();
                        }
                    });
                    eventListenerAdded = true;
                }

                const icon = this.querySelector(".navbar-toggler-icon");
                const isIconHidden = icon && window.getComputedStyle(icon).display === "none";

                if (isIconHidden) {
                    closeMenu();
                } else {
                    if (icon) icon.style.display = "none";
                    const closeBtn = this.parentElement?.querySelector(".navbar-close");
                    if (closeBtn) closeBtn.style.display = "block";

                    document.querySelector("body.homepage header .navbar")?.classList.add("mobile");
                    this.setAttribute("aria-expanded", "true");
                }
            });
        });

        // Initialize sublinks
        document.querySelectorAll(".navigation a.submenu").forEach(submenuLink => {
            submenuLink.addEventListener("click", function (e) {
                e.preventDefault();
                submenuClick(this, this.closest(".navigation"));
            });
        });

        resetMainMenu();

        // Close menu on click outside
        document.addEventListener("mouseup", (e) => {
            const container = document.querySelector(".nav-wrap");
            if (container && !container.contains(e.target) && container.querySelector(".sublinks")) {
                resetMainMenu();
            }
        });

        // Window resize event handler
        let windowWidth = window.innerWidth;
        let resizeTimeout;

        window.addEventListener("resize", () => {
            if (window.innerWidth !== windowWidth) {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    windowWidth = window.innerWidth;
                    resetMainMenu();
                }, 300);
            }
        });
    });
})();